import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import type { CreateTemplateRequestBody } from "@/common/types/templates";
import { Layout } from "@/components/layout";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { toast } from "@/hooks/use-toast";
import { useCreateTemplate } from "@/hooks/templates/useCreateTemplate";
import { container } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth.store";

export const TemplateCreate = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createTemplate, error, result, loading } = useCreateTemplate();
  const { userData } = useAuthStore();

  const handleCreateItem = (template: TemplateFormSchemaType): void => {
    const body = {
      userId: userData!.id,
      name: template.name,
      headers: template.headers,
      optionalText: template.optionalText,
      isDefault: template.isDefault,
    } as CreateTemplateRequestBody;

    void createTemplate(body);
  };

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        void navigate({ to: "/templates" });
        toast({
          variant: "default",
          title: t("templates.createdToast.successful.title"),
          description: t("templates.createdToast.successful.description"),
        });
      }
    }

    if (error && result != null && !loading) {
      toast({
        variant: "error",
        title: t("templates.createdToast.error.title"),
        description: t("templates.createdToast.error.description"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, error]);

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="min-h-screen bg-background p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          <TemplateForm
            submitText={t("templates.createTemplateForm.createTemplateButton")}
            onCancel={() => navigate({ to: "/templates" })}
            onSubmit={handleCreateItem}
          />
        </motion.div>
      </div>
    </Layout>
  );
};
